import React from 'react';

import { Box, Checkbox, FormControlLabel, OutlinedInput, Typography } from '@mui/material';

import { ShowFnOutput } from 'mui-modal-provider';

import { Reservation, ShippingStatus } from '../../../graphql/API';
import { useUpdateReservationMutation } from '../../../pages/ReservationDetail/components/useMutation';
import Icons from '../../../themes/icons';
import Button from '../../Button/Button';
import LoadingButton from '../../Button/LoadingButton';
import BaseDialog, { BaseDialogProps } from '../Base';

type Props = {
  reservation?: Reservation;
  modalRef: React.MutableRefObject<ShowFnOutput<BaseDialogProps> | undefined>;
  refetchReservationDetails?: () => void;
} & BaseDialogProps;

export const AddTrackingNumberDialog: React.FC<Props> = ({
  reservation,
  modalRef,
  refetchReservationDetails,
  ...rest
}) => {
  const [addItLater, setAddItLater] = React.useState(false);
  const [trackingNumber, setTrackingNumber] = React.useState('');

  React.useEffect(() => {
    if (trackingNumber) setAddItLater(false);
  }, [trackingNumber]);

  const handleChangeTracNo = React.useCallback((value?: string) => {
    setTrackingNumber(value || '');
  }, []);

  const handleChangeAddItLater = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      setAddItLater(event.target.checked);
    },
    [],
  );

  const cancelReservationMutation = useUpdateReservationMutation({
    onSuccess: () => {
      modalRef.current?.destroy();
      if (refetchReservationDetails) {
        refetchReservationDetails();
      }
    },
  });

  const onCancelHandle = React.useCallback(() => {
    modalRef.current?.destroy();
  }, [modalRef]);

  const onConfirmReservationHandle = () => {
    if (reservation?.id) {
      cancelReservationMutation.mutate({
        id: reservation.id,
        ...(reservation.shippingStatus === ShippingStatus.waiting && {
          shippingStatus: ShippingStatus.shipped,
        }),
        trackingNumber,
      });
    }
  };

  return (
    <BaseDialog width='465px' height={'420px'} titleComponent={<></>} {...rest}>
      <Box px={1}>
        <Typography fontSize={'18px'} fontWeight={500} mb={5}>
          Add tracking number
        </Typography>
        <Box>
          <Typography fontSize={'15px'} fontWeight={500} mb={'8px'}>
            Tracking number
          </Typography>
          <OutlinedInput
            placeholder='e.g. UPS 462380236462023'
            fullWidth
            value={trackingNumber}
            onChange={(e) => {
              handleChangeTracNo(e.target.value);
            }}
          />
          {reservation?.shippingStatus === ShippingStatus.waiting && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checkedIcon={<Icons.CheckboxIcon />}
                  sx={{ color: '#111', fontSize: '24px' }}
                  size='medium'
                  checked={addItLater}
                  onChange={handleChangeAddItLater}
                />
              }
              label={`I'll add it later`}
            />
          )}
        </Box>
        <Box
          display={'flex'}
          alignItems={'center'}
          mt={reservation?.shippingStatus === ShippingStatus.shipped ? 16 : 12}
          gap={6}
        >
          <Button
            variant='text'
            sx={{ fontSize: '15px', fontWeight: 500 }}
            onClick={onCancelHandle}
          >
            Cancel
          </Button>
          <LoadingButton
            fullWidth
            variant='contained'
            color='primary'
            sx={{ fontSize: '15px', fontWeight: 500, py: 1.5 }}
            disabled={!addItLater && !trackingNumber}
            onClick={onConfirmReservationHandle}
            loading={cancelReservationMutation.isPending}
          >
            {reservation?.shippingStatus === ShippingStatus.shipped
              ? 'Confirm tracking number'
              : 'Confirm shipment'}
          </LoadingButton>
        </Box>
      </Box>
    </BaseDialog>
  );
};
